import React from 'react';
import {
  IProductSectionContext,
  productSectionBlockName,
  ProductSectionContext,
} from '@gik/product-page/components/ProductSection/ProductSection';
import CheckoutLauncher from '@gik/checkout/components/CheckoutLauncher/CheckoutLauncher';
import { Button, FontIcon } from '@gik/ui';
import AddToWishlistPopover from '@gik/ui/gik/AddToWishlistPopover/AddToWishlistPopover';
import AddToFavouritesButton from '@gik/ui/gik/AddToFavouritesButton/AddToFavouritesButton';
import { useUserOwnedOrOrganizingPages } from '@gik/api/inkinds/userPages';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';

interface IProductActionsProps {
  showBuyButton?: boolean;
}

export default function ProductActions({ showBuyButton = true }: IProductActionsProps) {
  const {
    product,
    selectedCartItem,
    inkindRouteId,
    inkindPageViewHistory,
    type,
    hasErrors,
    setShowCustomError,
  } = React.useContext<IProductSectionContext>(ProductSectionContext);

  const userStore = useUserStore();
  const { data: inkindPages } = useUserOwnedOrOrganizingPages(userStore.id);

  // TODO: update to use SVG icon and share code with ProductTileToolbar
  let wishlistButtonIcon = <FontIcon name={'add'} />;

  // if the user only has one page we can show a minus icon for the
  // wishlist button if this product was already added to that page
  if (inkindPages?.length === 1) {
    const matchingPage = inkindPages[0];
    const isAdded = matchingPage.wishlistProductIds?.some(id => id === product.id);
    if (isAdded) {
      wishlistButtonIcon = <FontIcon name={'minus'} />;
    }
  }

  const bem = bemBlock(productSectionBlockName);

  return (
    <div className={bem('product-actions')}>
      {showBuyButton && (
        <CheckoutLauncher
          initiatedOn={type}
          skipProductsCheckoutStep
          hideProductsCheckoutStep
          initialInkindRouteId={inkindRouteId}
          inkindPageViewHistory={inkindPageViewHistory}
          cachedProducts={[product]}
          cart={selectedCartItem ? [selectedCartItem] : []}
          child={(onClick, isLoading) => (
            <Button
              variant="primary"
              loading={selectedCartItem !== null && isLoading}
              prepend={<FontIcon name="tag-o" />}
              className={bem('button-buy')}
              onClick={e => {
                if (hasErrors || selectedCartItem == null || selectedCartItem.price == undefined) {
                  setShowCustomError(true);
                } else {
                  onClick(e);
                }
              }}
            >
              Buy
            </Button>
          )}
        />
      )}

      {/* TODO: should be able to wishlist hidden products */}
      {!product.isHiddenProduct && (
        <AddToWishlistPopover
          productId={product.id}
          child={(onClick, isLoading) => (
            <Button
              loading={isLoading}
              variant={isLoading ? 'primary' : 'primary-outline'}
              prepend={wishlistButtonIcon}
              className={bem('button-wishlist', showBuyButton ? 'buy-button-visible' : null)}
              onClick={onClick}
            >
              Wishlist
            </Button>
          )}
        />
      )}

      {/* TODO: find a better way display of the two favorite buttons based on small/large / whether buy button is visible */}
      {/* TODO: should be able to favorite hidden products */}
      {!product.isHiddenProduct && (
        <AddToFavouritesButton
          type="products"
          itemId={product.id.toString()}
          child={(onClick, isLoading, isFavourited) => (
            <>
              <Button
                variant={'primary-outline'}
                prepend={<FontIcon name={isFavourited ? 'heart' : 'heart-o'} />}
                className={bem('button-save', showBuyButton ? 'large' : 'no-buy-button')}
                onClick={onClick}
              >
                {isFavourited ? 'Saved' : 'Save'}
              </Button>
              {showBuyButton && (
                <Button circle variant={'primary-plain'} className={bem('button-save', 'small')} onClick={onClick}>
                  <FontIcon name={isFavourited ? 'heart' : 'heart-o'} />
                </Button>
              )}
            </>
          )}
        />
      )}
    </div>
  );
}
